import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Trophy, Flame, Award, Dumbbell, Calendar, UserMinus, GitCompare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { TIER_COLORS, calculateTier, EvolveTier } from '@/types/evolve';
import { BADGES } from '@/types/badges';
import { format } from 'date-fns';

interface FriendProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  friendId: string;
  friendName: string;
  friendAvatar?: string | null;
  friendshipId?: string;
  onRemoveFriend?: (friendshipId: string, friendId: string) => Promise<boolean>;
  currentUserStats?: { totalWorkouts: number; daysActive: number; badges: number };
}

interface WorkoutSession {
  id: string;
  workout_type: string | null;
  completed_at: string;
  exercises_completed: number | null;
  duration_minutes: number | null;
}

interface PR {
  exercise: string;
  weight: number;
  date: string;
}

export function FriendProfileModal({
  isOpen,
  onClose,
  friendId,
  friendName,
  friendAvatar,
  friendshipId,
  onRemoveFriend,
  currentUserStats,
}: FriendProfileModalProps) {
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState<string[]>([]);
  const [recentWorkouts, setRecentWorkouts] = useState<WorkoutSession[]>([]);
  const [prs, setPrs] = useState<PR[]>([]);
  const [totalWorkouts, setTotalWorkouts] = useState(0);
  const [daysActive, setDaysActive] = useState(0);
  const [removing, setRemoving] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    if (!isOpen || !friendId) return;

    const fetchFriendData = async () => {
      setLoading(true);
      try {
        const { data: badgeData } = await supabase
          .from('user_badges')
          .select('badge_id')
          .eq('user_id', friendId);
        
        setBadges(badgeData?.map(b => b.badge_id) || []);

        const { data: workoutData } = await supabase
          .from('workout_sessions')
          .select('id, workout_type, completed_at, exercises_completed, duration_minutes')
          .eq('user_id', friendId)
          .order('completed_at', { ascending: false })
          .limit(5);

        setRecentWorkouts(workoutData || []);

        const { count } = await supabase
          .from('workout_sessions')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', friendId);

        setTotalWorkouts(count || 0);

        // Use secure function to get only non-sensitive friend profile data
        const { data: profile } = await supabase
          .rpc('get_friend_profile', { friend_user_id: friendId })
          .single();

        if (profile) {
          setDaysActive(profile.days_active || 0);
          if (profile.prs && Array.isArray(profile.prs)) {
            setPrs(profile.prs as unknown as PR[]);
          }
        }
      } catch (error) {
        console.error('Error fetching friend data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFriendData();
  }, [isOpen, friendId]);

  const handleRemoveFriend = async () => {
    if (!friendshipId || !onRemoveFriend) return;
    setRemoving(true);
    const success = await onRemoveFriend(friendshipId, friendId);
    if (success) {
      setShowConfirmRemove(false);
      onClose();
    }
    setRemoving(false);
  };

  const tierInfo = calculateTier(totalWorkouts);
  const unlockedBadges = BADGES.filter(b => badges.includes(b.id));

  const CompareItem = ({ label, yours, theirs }: { label: string; yours: number; theirs: number }) => {
    const youWin = yours > theirs;
    const tie = yours === theirs;
    return (
      <div className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-3">
          <span className={cn("font-bold", youWin && !tie && "text-green-500")}>{yours}</span>
          <span className="text-muted-foreground text-xs">vs</span>
          <span className={cn("font-bold", !youWin && !tie && "text-green-500")}>{theirs}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarImage src={friendAvatar || undefined} />
                <AvatarFallback>{friendName[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-display text-lg">{friendName}</p>
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded-full bg-gradient-to-r",
                  TIER_COLORS[tierInfo.currentTier as EvolveTier] || TIER_COLORS['Seed']
                )}>
                  {tierInfo.currentTier}
                </span>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {/* Compare Toggle */}
              {currentUserStats && (
                <Button
                  variant={showCompare ? "default" : "outline"}
                  size="sm"
                  className="w-full"
                  onClick={() => setShowCompare(!showCompare)}
                >
                  <GitCompare className="w-4 h-4 mr-2" />
                  {showCompare ? "Hide Comparison" : "Compare Stats"}
                </Button>
              )}

              {/* Comparison View */}
              {showCompare && currentUserStats && (
                <div className="bg-secondary/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-2 text-center">You vs {friendName}</p>
                  <CompareItem label="Workouts" yours={currentUserStats.totalWorkouts} theirs={totalWorkouts} />
                  <CompareItem label="Days Active" yours={currentUserStats.daysActive} theirs={daysActive} />
                  <CompareItem label="Badges" yours={currentUserStats.badges} theirs={badges.length} />
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 rounded-lg bg-secondary/50">
                  <Dumbbell className="w-4 h-4 mx-auto mb-1 text-primary" />
                  <p className="font-bold">{totalWorkouts}</p>
                  <p className="text-[10px] text-muted-foreground">Workouts</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-secondary/50">
                  <Flame className="w-4 h-4 mx-auto mb-1 text-orange-500" />
                  <p className="font-bold">{daysActive}</p>
                  <p className="text-[10px] text-muted-foreground">Days</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-secondary/50">
                  <Award className="w-4 h-4 mx-auto mb-1 text-yellow-500" />
                  <p className="font-bold">{badges.length}</p>
                  <p className="text-[10px] text-muted-foreground">Badges</p>
                </div>
              </div>

              {/* Badges */}
              {unlockedBadges.length > 0 && (
                <div>
                  <h3 className="font-semibold text-xs mb-2 flex items-center gap-2">
                    <Award className="w-3.5 h-3.5" />
                    Badges
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {unlockedBadges.slice(0, 6).map((badge) => (
                      <div
                        key={badge.id}
                        className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/50 text-[10px]"
                        title={badge.condition}
                      >
                        <span>{badge.icon}</span>
                        <span>{badge.name}</span>
                      </div>
                    ))}
                    {unlockedBadges.length > 6 && (
                      <div className="px-2 py-0.5 rounded-full bg-secondary/50 text-[10px] text-muted-foreground">
                        +{unlockedBadges.length - 6}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PRs */}
              {prs.length > 0 && (
                <div>
                  <h3 className="font-semibold text-xs mb-2 flex items-center gap-2">
                    <Trophy className="w-3.5 h-3.5" />
                    Personal Records
                  </h3>
                  <div className="space-y-1">
                    {prs.slice(0, 3).map((pr, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-1.5 rounded bg-secondary/30 text-xs"
                      >
                        <span className="truncate flex-1">{pr.exercise}</span>
                        <span className="font-bold text-primary ml-2">{pr.weight} lbs</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              {recentWorkouts.length > 0 && (
                <div>
                  <h3 className="font-semibold text-xs mb-2 flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" />
                    Recent Activity
                  </h3>
                  <div className="space-y-1">
                    {recentWorkouts.slice(0, 3).map((workout) => (
                      <div
                        key={workout.id}
                        className="flex items-center justify-between p-1.5 rounded bg-secondary/30 text-xs"
                      >
                        <div>
                          <p className="font-medium capitalize">{workout.workout_type || 'Workout'}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {format(new Date(workout.completed_at), 'MMM d')}
                          </p>
                        </div>
                        <div className="text-right text-[10px] text-muted-foreground">
                          {workout.exercises_completed && <p>{workout.exercises_completed} ex</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {recentWorkouts.length === 0 && prs.length === 0 && badges.length === 0 && (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  <p>No activity yet</p>
                </div>
              )}

              {/* Remove Friend Button */}
              {friendshipId && onRemoveFriend && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setShowConfirmRemove(true)}
                >
                  <UserMinus className="w-4 h-4 mr-2" />
                  Remove Friend
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmRemove} onOpenChange={setShowConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {friendName}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {friendName} from your friends? You'll need to send a new friend request to reconnect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFriend}
              disabled={removing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removing ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
